import { Injectable } from '@angular/core';

import { BaseService } from './base.service';

import { Destination } from '../../objects/destination';
import { Product } from '../../objects/product';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DateOfStay } from 'src/objects/date-of-stay';
import { LayoutPosition } from 'src/objects/layout-unit-position';
import { MappedArea } from 'src/objects/mapped-area';
import { InventoryUnit } from 'src/objects/inventory-unit';
import { TemplatedLayoutPosition } from 'src/objects/layout-position';
import { Landmark } from 'src/objects/layout-landmark';
import { Section } from '../../objects/section';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class LayoutService
  extends BaseService {
  createInstance = (attributes: LayoutPosition) => attributes;

  getAll(payload: {destination: Destination; product: Product; dateOfStay: DateOfStay}): Observable<LayoutPosition[]> {
    const url = `${ this.baseUrl }/public/layout_unit_positions/`;
    const {
      destination,
      dateOfStay
    } = payload;
    const params = {
      ['start_date']: format(dateOfStay.start!, 'yyyy-MM-dd'),
      ['destination_id']: `${destination.id}`,
    };

    return this.getPagedRequest<LayoutPosition, LayoutPosition>({ url, params, limit: 200 });
  }

  getAvailablePositions(payload: {destination: Destination; product: Product; dateOfStay: DateOfStay}): Observable<number[]> {
    const url = `${ this.baseUrl }/public/available_positions/`;
    const {
      destination,
      dateOfStay
    } = payload;

    return this.http
      .get<{identifier: string; valid_positions?: number[]}>(url, {
        params: {
          ['start_date']: format(dateOfStay.start!, 'yyyy-MM-dd'),
          ['end_date']: format(dateOfStay.end!, 'yyyy-MM-dd'),
          ['destination_id']: `${destination.id}`,
        },
      })
      .pipe(
        map(({ valid_positions }) => valid_positions ?? []),
      );
  }

  /**
   * NEW LAYOUTS
   */
  getTemplatedLayoutPositions(payload: {
    mappedArea: MappedArea;
    dateOfStay: DateOfStay;
  }): Observable<TemplatedLayoutPosition[]> {
    const url = `${ this.baseUrl }/public/layout_positions`;
    const { dateOfStay, mappedArea } = payload;

    const params = {
      ['start_date']: format(dateOfStay.start!, 'yyyy-MM-dd'),
      ['mapped_area_id']: `${mappedArea.id}`,
    };

    const mapTo = (attributes: TemplatedLayoutPosition) => attributes as TemplatedLayoutPosition;

    return this.getPagedRequest<TemplatedLayoutPosition, TemplatedLayoutPosition>({ url, params, mapTo, limit: 200 });
  }

  getTemplatedLayoutLandmarks(payload: {
    mappedArea: MappedArea;
    dateOfStay: DateOfStay;
  }): Observable<Landmark[]> {
    const url = `${ this.baseUrl }/public/landmarks`;
    const { dateOfStay, mappedArea } = payload;

    const params = {
      ['start_date']: format(dateOfStay.start!, 'yyyy-MM-dd'),
      ['mapped_area_id']: `${mappedArea.id}`,
    };
    const mapTo = (attributes: Landmark) => attributes as Landmark;

    return this.getPagedRequest<Landmark, Landmark>({ url, params, mapTo });
  }

  getTemplatedLayoutInventoryUnits(payload: {
    mappedArea: MappedArea;
    dateOfStay: DateOfStay;
  }): Observable<InventoryUnit[]> {
    const url = `${ this.baseUrl }/public/inventory_units`;
    const { dateOfStay, mappedArea } = payload;

    const params = {
      ['start_date']: format(dateOfStay.start!, 'yyyy-MM-dd'),
      ['end_date']: format(dateOfStay.end!, 'yyyy-MM-dd'),
      ['mapped_area_id']: `${mappedArea.id}`,
    };

    const mapTo = (attributes: InventoryUnit) => attributes as InventoryUnit;

    return this.getPagedRequest<InventoryUnit, InventoryUnit>({ url, params, mapTo, limit: 200 });
  }

  getTemplatedLayoutSections(payload: { mappedArea: MappedArea; dateOfStay: DateOfStay }): Observable<Section[]> {
    const url = `${ this.baseUrl }/public/layout_sections`;
    const { mappedArea , dateOfStay} = payload;
    const params = {
      ['start_date']: format(dateOfStay.start!, 'yyyy-MM-dd'),
      ['end_date']: format(dateOfStay.end!, 'yyyy-MM-dd'),
      ['mapped_area_id']: `${mappedArea.id}`,
    };

    const mapTo = (attributes: Section) => attributes as Section;

    return this.getPagedRequest<Section, Section>({ url, params, mapTo, limit: 200 });
  }
}
